import { format, parseISO } from 'date-fns';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuoteChangelog } from '../../hooks';

interface ChangelogEntry {
  _id?: string;
  user_name?: string;
  date?: string;
  changes?: Record<string, { from?: unknown; to?: unknown }>;
  action?: string;
}

interface QuoteChangelogProps {
  quoteId: string;
}

export function QuoteChangelog({ quoteId }: QuoteChangelogProps) {
  const { t } = useTranslation('quotes');
  const { data: changelog, isLoading } = useQuoteChangelog(quoteId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const entries = (changelog || []) as ChangelogEntry[];

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        {t('changelog.noEntries')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {entries.map((entry, index) => (
        <div key={entry._id || index} className="border rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">{entry.user_name || 'System'}</span>
            <span className="text-xs text-muted-foreground">
              {entry.date ? format(parseISO(entry.date), 'MMM d, yyyy HH:mm') : '—'}
            </span>
            {entry.action && (
              <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{entry.action}</span>
            )}
          </div>
          {entry.changes && Object.keys(entry.changes).length > 0 && (
            <div className="text-xs text-muted-foreground pl-5 space-y-0.5">
              {Object.entries(entry.changes).map(([field, change]) => (
                <div key={field}>
                  <span className="font-medium">{field}</span>:{' '}
                  <span className="line-through">{String(change.from ?? '—')}</span>
                  {' → '}
                  <span>{String(change.to ?? '—')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
